import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Pencil, Plus, ShieldCheck, UserMinus } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { apiMessage, fullName, pruneParams, useDebounced } from '../helpers';
import type { Role, Staff } from '../types';
import StaffFormDialog from './StaffFormDialog';
import StaffCredentialsDialog from './StaffCredentialsDialog';
import ConfirmDialog from './ConfirmDialog';

interface Props {
  /** Jump to the scope-assignment tab with this person already picked. */
  onManageRoles: (staffId: string) => void;
  /**
   * Raised after a create, an edit or a deactivation lands. The Settings tab
   * needs nothing — this panel re-reads its own list — but the setup wizard
   * embeds it as a step whose tick is read back from the API rather than
   * assumed from the click, so it has to know a write happened at all.
   */
  onChanged?: () => void;
}

/** Staff directory — search, filter, create/edit, deactivate and credentials. */
export default function StaffPanel({ onManageRoles, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const debouncedSearch = useDebounced(search);

  const params = useMemo(
    () => pruneParams({ limit: 100, search: debouncedSearch, roleId, isActive: activeFilter }),
    [debouncedSearch, roleId, activeFilter],
  );

  const staffQuery = useApi<unknown>('/users', params);
  const rolesQuery = useApi<unknown>('/roles', { limit: 200 });

  const staff = rowsOf<Staff>(staffQuery.data).rows;
  const roles = rowsOf<Role>(rolesQuery.data).rows;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [credentialsFor, setCredentialsFor] = useState<Staff | null>(null);
  const [deactivating, setDeactivating] = useState<Staff | null>(null);

  const canUpdate = can('users.update');

  const deactivate = async () => {
    if (!deactivating) return;
    try {
      await api.post(`/users/${deactivating.id}/deactivate`);
      toast.success(t('settings.staff.deactivated'));
      staffQuery.reload();
      onChanged?.();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('settings.staff.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </div>
        <Select
          className="w-auto min-w-[10rem]"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          aria-label={t('settings.staff.filterRole')}
        >
          <option value="">{t('settings.staff.allRoles')}</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
        <Select
          className="w-auto min-w-[9rem]"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          aria-label={t('settings.common.status')}
        >
          <option value="">{t('settings.common.allStatuses')}</option>
          <option value="true">{t('settings.common.active')}</option>
          <option value="false">{t('settings.common.inactive')}</option>
        </Select>

        {can('users.create') && (
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            {t('settings.staff.create')}
          </Button>
        )}
      </div>

      {staffQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : staffQuery.error ? (
        <ErrorState message={staffQuery.error} onRetry={staffQuery.reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('settings.staff.name')}</TH>
                <TH>{t('settings.staff.contact')}</TH>
                <TH>{t('settings.staff.roles')}</TH>
                <TH>{t('settings.common.status')}</TH>
                <TH>{t('settings.staff.lastLogin')}</TH>
                <TH className="text-end">{t('settings.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {staff.length === 0 ? (
                <TableEmpty colSpan={6}>{t('settings.staff.empty')}</TableEmpty>
              ) : (
                staff.map((person) => (
                  <TR key={person.id}>
                    <TD className="font-medium">{fullName(person)}</TD>
                    <TD className="text-ink-muted">
                      <div className="space-y-0.5">
                        {person.email && <div dir="ltr" className="text-start">{person.email}</div>}
                        {person.username && <div dir="ltr" className="text-start">@{person.username}</div>}
                        {person.phone && <div dir="ltr" className="text-start">{person.phone}</div>}
                        {!person.email && !person.username && !person.phone && (
                          <span>{t('settings.common.none')}</span>
                        )}
                      </div>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        {(person.roleAssignments ?? []).length === 0 ? (
                          <span className="text-ink-muted">
                            {t('settings.assignments.noRoles')}
                          </span>
                        ) : (
                          (person.roleAssignments ?? []).map((assignment) => (
                            <Badge key={assignment.id} tone="brand">
                              {assignment.role.name}
                              <span className="ms-1 opacity-70">
                                {t(`settings.scope.level.${assignment.scopeLevel}`)}
                              </span>
                            </Badge>
                          ))
                        )}
                      </div>
                    </TD>
                    <TD>
                      <Badge tone={person.isActive ? 'success' : 'danger'}>
                        {person.isActive
                          ? t('settings.common.active')
                          : t('settings.common.inactive')}
                      </Badge>
                    </TD>
                    <TD className="text-ink-muted">
                      {person.lastLoginAt
                        ? formatDateTime(person.lastLoginAt, i18n.language)
                        : t('settings.common.never')}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('common.edit')}
                            onClick={() => {
                              setEditing(person);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('settings.credentials.title')}
                            onClick={() => setCredentialsFor(person)}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          title={t('settings.tabs.assignments')}
                          onClick={() => onManageRoles(person.id)}
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </Button>
                        {canUpdate && person.isActive && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('settings.staff.deactivate')}
                            onClick={() => setDeactivating(person)}
                          >
                            <UserMinus className="h-4 w-4 text-red-600 dark:text-red-400" />
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <StaffFormDialog
        open={formOpen}
        staff={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          staffQuery.reload();
          onChanged?.();
        }}
      />

      <StaffCredentialsDialog
        open={credentialsFor !== null}
        staffId={credentialsFor?.id ?? null}
        staffName={credentialsFor ? fullName(credentialsFor) : ''}
        onClose={() => setCredentialsFor(null)}
        onSaved={staffQuery.reload}
      />

      <ConfirmDialog
        open={deactivating !== null}
        title={t('settings.staff.deactivate')}
        message={t('settings.staff.deactivateConfirm', {
          name: deactivating ? fullName(deactivating) : '',
        })}
        confirmLabel={t('settings.staff.deactivate')}
        onConfirm={deactivate}
        onClose={() => setDeactivating(null)}
      />
    </div>
  );
}
